import { Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, filter, of, Subscription, switchMap, take, timer } from 'rxjs';
import { DropdownMenusModule } from 'src/app/_metronic/partials';
import { SubjectService } from 'src/app/services/api/subjects/subject.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { SubjectDataSource, SubjectListResponse, SubjectModel, SubjectRow } from 'src/app/services/models/subjects/subject.model';
import { SharedModule } from 'src/shared/shared.module';
import {
  FormAddSubjectComponent,
  SubjectSaveAction,
  SubjectSaveFailedEvent,
} from '../../import/components/form-add-subject/form-add-subject.component';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { FormImportNewComponent } from '../../import/components/form-import-new/form-import-new.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';

type SubjectAction = 'create' | 'edit' | 'delete' | 'download-template' | 'import';
const PREVIEW_WORKLOAD_POLL_INTERVAL_MS = 2500;

@Component({
  selector: 'app-subjects',
  imports: [
    SharedModule,
    TranslateModule,
    DropdownMenusModule,
    ReactiveFormsModule,
    FormAddSubjectComponent,
    FormImportNewComponent,
  ],
  templateUrl: './subjects.component.html',
  styleUrl: './subjects.component.scss',
})
export class SubjectsComponent implements OnInit, OnDestroy {
  @ViewChild('manualStatusTemplate', { static: true })
  manualStatusTemplate!: TemplateRef<any>;
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('tableCellAction', { static: true })
  tableCellAction!: TemplateRef<any>;
  @ViewChild(FormAddSubjectComponent)
  subjectForm!: FormAddSubjectComponent;
  @ViewChild(FormImportNewComponent)
  importForm!: FormImportNewComponent;
  @ViewChild('importSubjectSuccessDialog')
  importSubjectSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteSubjectConfirmDialog')
  deleteSubjectConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('deleteSubjectSuccessDialog')
  deleteSubjectSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteSubjectErrorDialog')
  deleteSubjectErrorDialog!: ConfirmDialogComponent;
  @ViewChild('createSubjectSuccessDialog')
  createSubjectSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('createSubjectErrorDialog')
  createSubjectErrorDialog!: ConfirmDialogComponent;
  @ViewChild('editSubjectSuccessDialog')
  editSubjectSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('editSubjectErrorDialog')
  editSubjectErrorDialog!: ConfirmDialogComponent;

  form!: FormGroup;
  columns: any[] = [];
  subjects = signal<SubjectDataSource>(this.createSubjectDataSource([]));
  academicTerms = signal<AcademicTermYearModel[]>([]);
  tableLoading = signal(false);
  lastAction = signal<SubjectAction | null>(null);
  pageSizeOptions = [10, 20, 50];
  isDeleting = false;
  isDownloadingTemplate = false;
  isImporting = false;
  yearOptions: string[] = [];
  deleteSubjectErrorMessage = 'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง';
  createSubjectErrorMessage = 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง';
  editSubjectErrorMessage = 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';

  private fb = inject(FormBuilder);
  private subjectService = inject(SubjectService);
  private studyPeriodService = inject(StudyPeriodService);
  private authService = inject(AuthService);
  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private deletingSubjectId?: number;
  private filterSubscriptions: Subscription[] = [];
  private previewPollingSubscription?: Subscription;
  private previewPollingTermId?: number;
  private previewTerminalTermIds = new Set<number>();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    term: 'academic_term_name',
    year: 'academic_term_year',
    code: 'code',
    name: 'name',
  };

  // ENOT hotfix (release 1): the add-subject button is enabled only when the
  // SELECTED term+year (filter) has been synced from REG — not the session's
  // active term. Reads the academicTerms signal so the button updates when the
  // term list arrives async and whenever the filter changes.
  get isSelectedTermSynced(): boolean {
    if (this.form == null) {
      return false;
    }

    return this.selectedAcademicTerm()?.sync_at != null;
  }

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenFilters();
    this.loadAcademicTerms();
    this.loadSubjects();
  }

  ngOnDestroy(): void {
    this.filterSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.stopPreviewPolling();
  }

  onSearch(): void {
    this.loadSubjects(1);
  }

  onAddSubject(): void {
    this.lastAction.set('create');
    this.subjectForm.openModal();
  }

  onEditSubject(row: SubjectRow): void {
    this.lastAction.set('edit');
    this.subjectForm.openModal(row.id);
  }

  onSubjectSaved(action: SubjectSaveAction): void {
    this.loadSubjects(action === 'create' ? 1 : this.currentPage);

    if (action === 'create') {
      this.createSubjectSuccessDialog.open();
      return;
    }

    this.editSubjectSuccessDialog.open();
  }

  onSubjectSaveFailed({ action, message }: SubjectSaveFailedEvent): void {
    if (action === 'create') {
      this.createSubjectErrorMessage = message;
      this.createSubjectErrorDialog.open();
      return;
    }

    this.editSubjectErrorMessage = message;
    this.editSubjectErrorDialog.open();
  }

  onDeleteSubject(row: SubjectRow): void {
    if (!row.canDelete) {
      return;
    }

    this.lastAction.set('delete');
    this.deletingSubjectId = row.id;
    this.deleteSubjectConfirmDialog.open();
  }

  onDownloadTemplate(): void {
    this.lastAction.set('download-template');

    if (this.isDownloadingTemplate) {
      return;
    }

    this.isDownloadingTemplate = true;

    this.subjectService.exportTemplate().subscribe({
      next: (blob) => {
        this.downloadBlob(blob, 'subjects_template.xlsx');
      },
      error: (err) => {
        console.error(err);
        this.isDownloadingTemplate = false;
      },
      complete: () => {
        this.isDownloadingTemplate = false;
      },
    });
  }

  onImport(): void {
    this.lastAction.set('import');
  }

  onImportFile(file: File): void {
    this.lastAction.set('import');

    if (this.isImporting) {
      return;
    }

    this.isImporting = true;

    this.subjectService.importSubjectsTemplate(file).subscribe({
      next: () => {
        this.importForm.clearFileSelection();
        this.loadSubjects(1);
        this.importForm.closeAfterImportSuccess(() => {
          this.importSubjectSuccessDialog.open();
        });
      },
      error: (err) => {
        this.importForm.showImportError(err.error);
        this.isImporting = false;
      },
      complete: () => {
        this.isImporting = false;
      },
    });
  }

  onConfirmDeleteSubject(): void {
    if (this.deletingSubjectId == null || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.subjectService.deleteSubject(this.deletingSubjectId).subscribe({
      next: () => {
        this.deleteSubjectConfirmDialog.close();
        this.deletingSubjectId = undefined;
        this.loadSubjects();
        this.deleteSubjectSuccessDialog.open();
      },
      error: (err) => {
        console.error(err);
        this.deleteSubjectConfirmDialog.close();
        this.deleteSubjectErrorMessage = this.getApiErrorMessage(
          err,
          'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง'
        );
        this.deletingSubjectId = undefined;
        this.deleteSubjectErrorDialog.open();
        this.isDeleting = false;
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }

  onCancelDeleteSubject(): void {
    this.deletingSubjectId = undefined;
  }

  loadSubjects(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;
    const { academic_term_name, year, code, name } = this.form.getRawValue();

    this.subjectService.getSubjectsList({
      academic_term_name,
      academic_term_year: this.toChristianYear(year),
      ...(this.isSelectedTermSynced ? {} : { preview: true }),
      code,
      name,
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (res) => {
        this.subjects.set(this.createSubjectDataSource(res));
        this.syncPreviewPolling(res.total);
        this.tableLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.subjects.set(this.createSubjectDataSource([]));
        this.tableLoading.set(false);
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.subjects().total_page || page === this.currentPage) {
      return;
    }

    this.loadSubjects(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadSubjects(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadSubjects(1);
  }

  private loadAcademicTerms(): void {
    const subscription = this.studyPeriodService.getAcademicTermYears().subscribe({
      next: (terms) => {
        this.academicTerms.set(terms);
        this.syncPreviewPolling(this.subjects().total);
      },
      error: (err) => {
        console.error(err);
        this.academicTerms.set([]);
      },
    });

    this.filterSubscriptions.push(subscription);
  }

  private selectedAcademicTerm(): AcademicTermYearModel | undefined {
    const { academic_term_name, year } = this.form.getRawValue();
    const christianYear = this.toChristianYear(year);

    if (christianYear == null) {
      return undefined;
    }

    return this.academicTerms().find((term) =>
      String(term.name).trim() === String(academic_term_name ?? '').trim()
      && String(term.year) === christianYear);
  }

  private syncPreviewPolling(total: number): void {
    const term = this.selectedAcademicTerm();

    if (term == null || term.sync_at != null || total > 0) {
      this.stopPreviewPolling();
      return;
    }

    if (this.previewTerminalTermIds.has(term.id) || this.previewPollingTermId === term.id) {
      return;
    }

    this.stopPreviewPolling();
    this.previewPollingTermId = term.id;
    this.previewPollingSubscription = timer(0, PREVIEW_WORKLOAD_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.studyPeriodService.getPreviewWorkloadStatus(term.id).pipe(
        catchError(() => of({ preview: null })),
      )),
      filter(({ preview }) => preview?.status === 'succeeded' || preview?.status === 'failed'),
      take(1),
    ).subscribe(({ preview }) => {
      this.previewTerminalTermIds.add(term.id);
      this.stopPreviewPolling();

      if (preview?.status === 'succeeded' && this.selectedAcademicTerm()?.id === term.id) {
        this.loadSubjects(this.currentPage);
      }
    });
  }

  private stopPreviewPolling(): void {
    this.previewPollingSubscription?.unsubscribe();
    this.previewPollingSubscription = undefined;
    this.previewPollingTermId = undefined;
  }

  private initForm(): void {
    const defaultAcademicTerm = this.authService.currentUser()?.academic_term;
    const defaultYear = defaultAcademicTerm?.year == null
      ? String(this.getDefaultBuddhistYear())
      : String(this.toBuddhistYear(defaultAcademicTerm.year));

    this.yearOptions = this.buildYearOptions();
    this.form = this.fb.group({
      academic_term_name: [defaultAcademicTerm?.name ?? ''],
      year: [defaultYear],
      code: [''],
      name: [''],
    });
  }

  private listenFilters(): void {
    const searchFields = ['code', 'name'];
    const selectFields = ['academic_term_name', 'year'];

    searchFields.forEach((field) => {
      const subscription = this.form.get(field)?.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadSubjects(1));

      if (subscription != null) {
        this.filterSubscriptions.push(subscription);
      }
    });

    selectFields.forEach((field) => {
      const subscription = this.form.get(field)?.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(() => this.loadSubjects(1));

      if (subscription != null) {
        this.filterSubscriptions.push(subscription);
      }
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'manualStatus',
        name: '',
        sortable: false,
        cellTemplate: this.manualStatusTemplate,
        width: 24,
        minWidth: 24,
        maxWidth: 24,
      },
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
      {
        prop: 'term',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 140,
        minWidth: 140,
        maxWidth: 140,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        prop: 'code',
        name: 'รหัสวิชา',
        sortable: true,
        width: 170,
        minWidth: 170,
      },
      {
        prop: 'name',
        name: 'ชื่อรายวิชา',
        sortable: true,
        width: 300,
        minWidth: 300,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.tableCellAction,
        width: 104,
        minWidth: 104,
        maxWidth: 104,
        cellClass: 'action-cell',
        headerClass: 'action-header',
      },
    ];
  }

  private createSubjectDataSource(response: SubjectListResponse | SubjectModel[]): SubjectDataSource {
    const items = Array.isArray(response) ? response : response.data;
    const page = Array.isArray(response) ? 1 : response.page;
    const limit = Array.isArray(response) ? this.currentLimit : response.limit;
    const total = Array.isArray(response) ? items.length : response.total;
    const totalPage = Array.isArray(response) ? Math.max(1, Math.ceil(total / limit)) : response.total_pages;
    const data = items.map((item) => ({
      id: item.id,
      sequence: item.sequence,
      term: item.academic_term?.name ?? '-',
      year: item.academic_term?.year != null
        ? this.toBuddhistYear(item.academic_term.year)
        : 0,
      code: item.code,
      name: item.name,
      canDelete: item.can_delete,
      isSynced: item.is_synced,
    }));

    return {
      data,
      limit,
      offset: (page - 1) * limit,
      page,
      total,
      total_page: totalPage,
    };
  }

  private toChristianYear(value: unknown): string | null {
    if (value == null || value === '' || value === 0 || value === '0') {
      return null;
    }

    const year = Number(value);

    if (!Number.isFinite(year) || year <= 0) {
      return null;
    }

    return String(year > 2400 ? year - 543 : year);
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private getDefaultBuddhistYear(): number {
    const sessionYear = this.authService.currentUser()?.academic_term?.year;
    const normalizedSessionYear = Number(sessionYear);

    if (sessionYear == null || !Number.isFinite(normalizedSessionYear) || normalizedSessionYear <= 0) {
      return this.toBuddhistYear(new Date().getFullYear());
    }

    return this.toBuddhistYear(normalizedSessionYear);
  }

  private buildYearOptions(): string[] {
    const baseYear = this.getDefaultBuddhistYear();

    return [baseYear + 1, baseYear, baseYear - 1].map((year) => String(year));
  }

  private getApiErrorMessage(error: unknown, fallbackMessage: string): string {
    const normalizedError = this.normalizeErrorBody(error);

    if (typeof normalizedError === 'string' && normalizedError.trim() !== '') {
      return normalizedError;
    }

    if (normalizedError !== null && typeof normalizedError === 'object') {
      const errorRecord = normalizedError as Record<string, unknown>;
      const errors = errorRecord['errors'];
      const errorMessage = errorRecord['error'];
      const message = errorRecord['message'];

      if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((item) => String(item)).join('\n');
      }

      if (typeof errors === 'string' && errors.trim() !== '') {
        return errors;
      }

      if (typeof errorMessage === 'string' && errorMessage.trim() !== '') {
        return errorMessage;
      }

      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
    }

    return fallbackMessage;
  }

  private normalizeErrorBody(error: unknown): unknown {
    if (error !== null && typeof error === 'object' && 'error' in error) {
      return this.normalizeErrorBody((error as { error?: unknown }).error);
    }

    if (typeof error !== 'string') {
      return error;
    }

    try {
      return JSON.parse(error);
    } catch {
      return error;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
